import React, { JSX, useState, useEffect } from 'react';
import {
    useRecordContext,
    useGetMany,
    Confirm,
    useGetOne
} from 'react-admin';
import { useSubscriptionGridLoader, useUpdateActiveSubscription, useActiveSubscriptionCancel } from "./hooks/subscription-manager-container.hooks";
import './styles/subscription-manager.scss';
import { CustomerAccount, Subscription } from "../../../../types/entities";
import { Add, Close, CancelPresentation } from '@mui/icons-material';
import { pricingTiers, currency, seatCounts, convertCurrencyToSymbol } from "../../../../defaults/defaults";
import { useGetSubscriptionTiers } from "../../../global-hooks/default-system-hooks";
import {
    Button,
    CircularProgress,
    Switch
} from '@mui/material';
import { SubscriptionManagerCheckoutDrawer } from "./components/SubscriptionManagerCheckoutDrawer";
import { SubscriptionManagerMiniCard } from "./components/SubscriptionManagerMiniCard";
import { SubscriptionManagerActiveSubscriptionDataPanel } from "./components/SubscriptionManagerActiveSubscriptionDataPanel";
import { SubscriptionManagerDiscountPanel } from "./components/SubscriptionManagerDiscountPanel";

type SubscriptionManagementItemType = {
    product: string,
    price: string,
    seats: number,
    customPrice: number,
    customSeats: number,
    usingCustomData: boolean,
    tier: string
};

export const SubscriptionManagerContainer: () => JSX.Element = (): JSX.Element => {
    const { loadingData, gridItems } = useSubscriptionGridLoader();
    const { loadedSubscriptionTiers, loadingSubscriptionTiers } = useGetSubscriptionTiers();
    const { processingSubscriptionUpdater, subscriptionUpdated, updateSubscriptionError, processSubscriptionUpdate } = useUpdateActiveSubscription();
    const { processingCancel, cancelSubscription, cancelError, isCancelled } = useActiveSubscriptionCancel();
    const record: CustomerAccount = useRecordContext();
    // console.log(record);
    const [activeSubscription, setActiveSubscription] = useState<Subscription>();
    const [showCreatePanel, setShowCreatePanel] = useState<boolean>(false);
    const [activeSubscriptionProductData, setActiveSubscriptionProductData] = useState<any>([]);
    const [activeFrequency, setActiveFrequency] = useState<string>('month');
    // const [activePrice, setActivePrice] = useState<number>(0);
    const [activeCurrency, setActiveCurrency] = useState<string>('gbp');
    const [toggleCheckoutPanel, setToggleCheckoutPanel] = useState<boolean>(false);
    const [showUpdateConfirm, setShowUpdateConfirm] = useState<boolean>(false);
    const [subscriptionChanged, setSubscriptionChanged] = useState<boolean>(false);
    const [ disableUpgradeProration, setDisableUpgradeProration ] = useState<boolean>(false);
    const [ showCancelConfirmation, setShowCancelConfirmation ] = useState<boolean>(false);
    const [ showChargeHelperText, setShowChargeHelperText ] = useState<boolean>(false);
    const [ showRefundChargeHelperText, setShowRefundChargeHelperText ] = useState<boolean>(false);
    const [ enableDowngradeRefund, setEnableDowngradeRefund ] = useState<boolean>(false);
    const [ editTab, setEditTab ] = useState<number>(1);
    const [activeDiscounts, setActiveDiscounts] = useState<string[]>([]);
    const [enabledCreateTrial, setEnabledCreateTrial] = useState<boolean>(false);

    const { data: couponData, isPending: isPendingCoupon, error: errorCoupon, refetch } = useGetOne('coupons', { id: activeDiscounts[0] ?? null });

    const { data, isPending, error } = useGetMany(
        'subscriptions',
        { ids: record.subscriptions }
    );

    let activePrice: number = 0;

    function handleCreateTrialToggle(): void {
        setEnabledCreateTrial(!enabledCreateTrial);
    }

    function handleActiveDiscountChange(discountId: string, type: string = 'add'): void {
        // debugger;
        setActiveDiscounts( (prev: string[]): string[] => {
            if (type === 'add') {
                return prev.includes(discountId) ? prev : [...prev, discountId];
            } else {
                return prev.includes(discountId) ? prev.filter( (id: string): boolean => id !== discountId) : prev;
            }
        });
        setSubscriptionChanged(true);
    }

    function handleEditTabChange(num: number): void {
        setEditTab(num);
    }

    function handleDowngradeRefundToggle(): void {
        setEnableDowngradeRefund(!enableDowngradeRefund);
    }

    function toggleRefundChargeHelperText(): void {
        setShowRefundChargeHelperText(!showRefundChargeHelperText)
    }

    function toggleChargerHelperText(): void {
        setShowChargeHelperText(!showChargeHelperText);
    }

    function toggleCancelConfirm(): void {
        if (!showCancelConfirmation) {
            // Focus left on the trigger is hidden from assistive technology while the dialog is open,
            // and Chrome logs "Blocked aria-hidden" for it (HQ-50).
            (document.activeElement as HTMLElement|null)?.blur();
        }
        setShowCancelConfirmation(!showCancelConfirmation);
    }

    function handleUpgradeProrationToggle(): void {
        setDisableUpgradeProration(!disableUpgradeProration);
    }

    function toggleUpdateConfirm(): void {
        if (!showUpdateConfirm) {
            // Focus left on the trigger is hidden from assistive technology while the dialog is open,
            // and Chrome logs "Blocked aria-hidden" for it (HQ-50).
            (document.activeElement as HTMLElement|null)?.blur();
        }
        setShowUpdateConfirm(!showUpdateConfirm);
    }

    function cancelUpdate(): void {
        if (activeSubscription) {
            const originalItems: any = activeSubscription?.configuration;
            setActiveSubscriptionProductData(originalItems);
            setSubscriptionChanged(false);
            const originalCoupons: string[] = activeSubscription?.coupons;
            setActiveDiscounts(originalCoupons)
        }
    }

    function translateTierLevel(level: string): string {
        let returnTier: string = '';
        for (let i: number = 0; i < pricingTiers.length; i++) {
            const tier: { id: string, name: string } = pricingTiers[i];
            if (tier.id === level) {
                returnTier = tier.name;
                break;
            }
        }
        return returnTier;
    }

    function translatePriceTier(tierId: string): string {
        let returnTier: string = '';
        if (loadedSubscriptionTiers) {
            for (let i: number = 0; i < loadedSubscriptionTiers?.length; i++) {
                const tier: { id: string, name: string } = loadedSubscriptionTiers[i];
                if (tier.id === tierId) {
                    returnTier = tier.name;
                    break;
                }
            }
        }

        return returnTier;
    }

    function translateCurrencyCode(code: string): string {
        let returnCurrency: string = '';
        for (let i: number = 0; i < currency.length; i++) {
            const cur: { id: string, name: string } = currency[i];
            if (cur.id === code) {
                returnCurrency = cur.name;
                break;
            }
        }

        return returnCurrency;
    }

    function isActiveSubscriptionProduct(product: string): boolean {
        let returnValue: boolean = false;
        const activeProducts: any = activeSubscriptionProductData;
        for (let i: number = 0; i < activeProducts?.length; i++) {
            const currentProduct: any = activeProducts[i];
            if (currentProduct.product === product) {
                returnValue = true;
                break;
            }
        }
        return returnValue;
    }

    function handleCheckoutPanelToggle(value: boolean): void {
        setToggleCheckoutPanel(value);
    }

    function handleFrequencyToggle(event: any) :void {
        setActiveFrequency(event.target.value);
    }

    function handleCreatePanelToggle(): void {
        const newVal: boolean = !showCreatePanel;
        setShowCreatePanel(newVal);
        if (data && data.length > 0 && newVal === false ) {
            const subscription: Subscription = data[(data.length - 1)];
            setActiveSubscription(subscription);
            setShowCreatePanel(false);
            setActiveSubscriptionProductData(subscription?.configuration);
            setActiveFrequency(subscription?.frequency);
            setActiveDiscounts(subscription?.coupons);
        } else {
            setActiveSubscription(undefined);
            setActiveSubscriptionProductData([]);
            setActiveFrequency('month');
            // HQ-37. No discount by default: coupon 6 was hardcoded here and does not exist.
            setActiveDiscounts([]);
        }
    }

    function handleCurrencyToggle(event: any) :void {
        setActiveCurrency(event.target.value);
    }


    function handleActiveSubscriptionProductUpdate(prod: SubscriptionManagementItemType): void {
        let currentProducts: any = activeSubscriptionProductData;
        let newProducts: SubscriptionManagementItemType[] = [];
        const items: any = gridItems ?? { products: [] };
        for (let i: number = 0; i < currentProducts.length; i++) {
            const currentProduct: any = currentProducts[i];
            const fullProducts: any = items.products.filter( (item: any): boolean => item.id === currentProduct.product);
            let fullProduct: any;
            if (fullProducts && fullProducts.length > 0) {
                fullProduct = fullProducts[0];
            } else {
                fullProduct = {
                    tier: -1
                }
            }
            if (prod.product === currentProduct.product) {
                continue;
            }
            if (prod.tier !== fullProduct.tier && fullProduct.tier !== '6' && prod.tier !== '6') {
                continue;
            }
            newProducts.push(currentProduct);
        }
        newProducts.push(prod);
        if ( (newProducts.filter( (val: any): boolean => !currentProducts.includes(val))).length > 0 ) {
            setSubscriptionChanged(true);
        }
        setActiveSubscriptionProductData(newProducts);
    }

    function handleActiveSubscriptionProductRemove(prod: SubscriptionManagementItemType): void {
        let currentProducts: any = activeSubscriptionProductData;
        let newProducts: SubscriptionManagementItemType[] = [];
        for (let i: number = 0; i < currentProducts.length; i++) {
            const currentProduct: any = currentProducts[i];
            if (prod.product === currentProduct.product || prod.tier !== '6') {
                continue;
            }
            newProducts.push(currentProduct);
        }
        setSubscriptionChanged(true);
        setActiveSubscriptionProductData(newProducts);
    }

    function handleUpdateActiveSubscriptionProductItem(prod: SubscriptionManagementItemType): void {
        let currentProducts: any = activeSubscriptionProductData;
        let newProducts: SubscriptionManagementItemType[] = [];

        newProducts.push(prod);
        for (let i: number = 0; i < currentProducts.length; i++) {
            const currentProduct: any = currentProducts[i];
            if (prod.product !== currentProduct.product) {
                newProducts.push(currentProduct);
            }
        }
        if ( (newProducts.filter( (val: any): boolean => !currentProducts.includes(val))).length > 0 ) {
            setSubscriptionChanged(true);
        }
        setActiveSubscriptionProductData(newProducts);
    }

    function handleSubscriptionSelectToggle(event: any) :void {
        if (!isPending && data.length > 0) {
            const id: number = event.target.value;
            for (const item of data) {
                if (item.id === parseInt(String(id))) {
                    setActiveSubscription(item);
                    setActiveSubscriptionProductData(item.configuration);
                }
            }
        }
    }

    function determineMiniNameFromId(datum: any): JSX.Element {
        let returnName: string = '';
        let price: number = 0;
        let allPrice: number = activePrice;
        let seats: number = 3;
        let discount: string = '';

        for (let j: number = 0; j < gridItems?.products?.length; j++) {
            const prod: any = gridItems.products[j];
            if (prod.id === datum.product) {
                seats = datum.seats ?? 3;
                if (datum.usingCustomData === true) {
                    price = (datum.customPrice * datum.customSeats);
                } else {
                    price = (prod.prices[activeFrequency].price * datum.seats);
                }
                if (couponData?.appliesToProducts?.includes(prod.id.toString()) || (couponData && couponData.appliesToProducts?.length <= 0) ) {
                    discount = couponData?.amountOff ?? 0;
                    if (couponData?.discountType === 'percent') {
                        price = price * (parseFloat(discount) / 100);
                        discount = discount + '%'
                    } else {
                        price = price - parseFloat(discount);
                        discount = activeCurrency + discount;
                    }
                }

                allPrice = allPrice + price;
                returnName = prod.name
                break;
            }
        }
        if (allPrice !== activePrice) {
            activePrice = (allPrice);
        }
        return (
            <div className="data">
                <span className="name">{ returnName }</span>
                <span>{ convertCurrencyToSymbol(activeCurrency) + price.toFixed(2) }</span>
                <span>{ discount }</span>
                <span>{ seats }</span>
            </div>
        );
    }

    function calculateTotalAmount(): string {
        let amount: number = activePrice;
        if (couponData?.id) {
            if (couponData?.discountType === 'fixed') {
                amount = amount - parseFloat(couponData?.amountOff);
            } else {
                amount = amount - (amount * ( parseFloat(couponData?.amountOff) / 100 ) );
            }
        }
        // This applied the discount and then returned activePrice, so the Total
        // shown to the operator always equalled the Subtotal no matter what
        // discount was attached. A fixed discount larger than the subtotal
        // would also have shown a negative total.
        return Math.max(0, amount).toFixed(2);
    }

    function getDiscountAmount(): string {
        let discountAmount: string = 'N/A';
        if (couponData?.id) {
            if (couponData?.discountType === 'fixed') {
                discountAmount = convertCurrencyToSymbol(activeCurrency) + parseFloat(couponData?.amountOff)?.toFixed(2);
            } else {
                discountAmount = couponData?.amountOff + '%';
            }
        }
        return discountAmount;
    }

    function updateActiveSubscription(): void {
        toggleUpdateConfirm();
        const payload = {
            subscription: activeSubscription?.id,
            products: activeSubscriptionProductData,
            customerAccount: record.id,
            disableUpgradeProration: disableUpgradeProration,
            enabledDowngradeRefundProration: enableDowngradeRefund,
            activeDiscounts: activeDiscounts
        };
        processSubscriptionUpdate(payload);
    }

    function cancelActiveSubscription(): void {
        toggleCancelConfirm();
        if (activeSubscription !== undefined) {
            cancelSubscription(activeSubscription.id);
        }
    }

    useEffect( (): void => {
        // TODO - rework this, t'is plop
        if (!isPending && data && data.length > 0 &&
            activeSubscription === undefined && showCreatePanel === false
        ) {
            const subscription: Subscription = data[(data.length - 1)];
            setActiveSubscription(subscription);
            setShowCreatePanel(false);
            setActiveSubscriptionProductData(subscription?.configuration);
            setActiveFrequency(subscription?.frequency);
            setActiveDiscounts(subscription.coupons ?? []);
        }
        if (!isPending && data && data.length <= 0) {
            setShowCreatePanel(true);
            setActiveDiscounts([]);
        }

    }, [isPending, data, activeSubscription, showCreatePanel] );

    return (
        <div className="subscription__manager">
            { (loadingData) &&
                <div className="loader">
                    <img src="/images/icon/nn-logo.png" alt="noknok logo" />
                </div>
            }

            { (!loadingData) &&
                <div className="manager__grid">
                    { gridItems?.products?.map((item: any, index: number): JSX.Element => {
                        return (
                            <div key={ "subscription_grid_item_" + index }
                                className={ "grid__item " + (isActiveSubscriptionProduct(item.id) ? ' active' : '') }>
                                <div className={ "item__title" }>
                                    <span>{ item.name }</span>
                                </div>
                                <div className={ "item__description" }>
                                    <p>{ item.description ?? 'Need to add description' }</p>
                                </div>
                                { (item.status === 'active') &&
                                    <div className="item__tiers">
                                        <SubscriptionManagerMiniCard
                                            // key={ "nok_mini_subscription_item_" + idx }
                                            product={ item }
                                            updatingSubscription={ processingSubscriptionUpdater }
                                            activeFrequency={ activeFrequency }
                                            loadedSubscriptionTiers={ loadedSubscriptionTiers }
                                            handleActiveSubscriptionProductUpdate={ handleActiveSubscriptionProductUpdate }
                                            isActiveSubscriptionProduct={ isActiveSubscriptionProduct }
                                            handleUpdateActiveSubscriptionProductItem={ handleUpdateActiveSubscriptionProductItem }
                                            activeSubscription={ activeSubscription }
                                            activeSubscriptionProductData={ activeSubscriptionProductData }
                                            subscriptionChanged={ subscriptionChanged }
                                            activeCurrency={ activeCurrency }
                                            handleActiveSubscriptionProductRemove={ handleActiveSubscriptionProductRemove }
                                        />
                                    </div>
                                }
                                { (item.status === 'inactive') &&
                                    <div className="item__unavailable">
                                        <span>{ "Mini not available for subscription" }</span>
                                    </div>
                                }
                            </div>
                        );
                    } ) }
                </div>
            }
            { /* ------- SUBSCRIPTION CONTROLS ------- */ }
            <div className="manager__controls">
                <div className="controls__subscription-selector">
                    <select value={ activeSubscription?.id }
                            onChange={ handleSubscriptionSelectToggle }
                            disabled={ showCreatePanel }>
                        { (!isPending && data.length <= 0) &&
                            <option>{ "No Subscriptions Found" }</option>
                        }
                        { (!isPending && data.length > 0) &&
                            <>
                                { data.map((item: any, index: number): JSX.Element => {
                                    return (
                                        <option key={ "subscription_item_" + index } value={ item.id }>{ "Subscription " + (index + 1) }</option>
                                    );
                                } )  }
                            </>
                        }
                    </select>
                    <button type="button"
                            disabled={ (activeSubscription !== undefined) }
                            // disabled={ processingSubscriptionUpdater || ( activeSubscription?.status === 'canceled' ) || processingCancel }
                            onClick={ handleCreatePanelToggle }
                            title={ showCreatePanel ? 'Close Create Panel' : 'Show Create Panel' } >
                        { (showCreatePanel) &&
                            <Close />
                        }
                        { (!showCreatePanel) &&
                            <Add />
                        }
                    </button>
                </div>
                <div className="controls__subscription">
                    { /* ------ CREATE SUBSCRIPTION PANEL ------ */ }
                    { (showCreatePanel) &&
                        <div className="subscription__create">
                            <span className="create__title" >{ "Create new subscription for account" }</span>
                            <div className="create__frequency">
                                <span>{ 'Subscription Frequency' }</span>
                                <select value={ activeFrequency } onChange={ (e: any): void => handleFrequencyToggle(e) } >
                                    <option value="month">{ "Monthly" }</option>
                                    <option value="year">{ "Yearly" }</option>
                                </select>
                            </div>
                            <div className="create__currency">
                                <span>{ 'Subscription Currency' }</span>
                                <select value={ activeCurrency } onChange={ (e: any): void => handleCurrencyToggle(e) } >
                                    { currency.map( ( item: any, index: number ): JSX.Element => {
                                        return (
                                            <option value={ item.id } key={ index }>{ item.name }</option>
                                        );
                                    } ) }
                                </select>
                            </div>
                            <div className="create__discount">
                                <span className="items__title">{ "Discounts" }</span>
                                <SubscriptionManagerDiscountPanel
                                    handleActiveDiscountChange={ handleActiveDiscountChange }
                                    activeDiscounts={ activeDiscounts }
                                />
                            </div>
                            <div className="create__items">
                                <span className="items__title">{ "Subscription Items" }</span>
                                <div className="items__header">
                                    <span>{ "Name" }</span>
                                    <span>{ "Price" }</span>
                                    <span>{ "Discount" }</span>
                                    <span>{ "Seats/Quantity" }</span>
                                </div>
                                <div className="items__items">
                                    { activeSubscriptionProductData.map( (datum: any, index: number): JSX.Element => {
                                        return (
                                            <div className="items__item" key={ "new_subscription_items" + index }>
                                                { determineMiniNameFromId(datum) }
                                            </div>
                                        );
                                    } ) }
                                </div>
                                <div className="items__allPrice">
                                    <div className="allPrice__price">
                                        <span>{ `Subtotal` }</span>
                                        <span>{ convertCurrencyToSymbol(activeCurrency) + activePrice.toFixed(2) }</span>
                                    </div>
                                    <div className="allPrice__price center">
                                        <span>{ `Discount` }</span>
                                        <span>{ getDiscountAmount() }</span>
                                    </div>
                                    <div className="allPrice__price right">
                                        <span>{ `Total` }</span>
                                        <span>{ convertCurrencyToSymbol(activeCurrency) + calculateTotalAmount() }</span>
                                    </div>
                                </div>
                            </div>
                            <div className="create__actions">
                                <SubscriptionManagerCheckoutDrawer toggleCheckoutPanel={ toggleCheckoutPanel }
                                                                   handleCheckoutPanelToggle={ handleCheckoutPanelToggle }
                                                                   customerAccount={ record }
                                                                   activeSubscriptionProductData={ activeSubscriptionProductData }
                                                                   activeFrequency={ activeFrequency }
                                                                   activeCurrency={ activeCurrency }
                                                                   handleCreatePanelToggle={ handleCreatePanelToggle }
                                                                   activeDiscounts={ activeDiscounts }
                                                                   enabledCreateTrial={ enabledCreateTrial }
                                />
                            </div>
                        </div>
                    }
                    { /* ------ EDIT SUBSCRIPTION PANEL ------ */ }
                    { (!showCreatePanel) &&
                        <div className="subscription__edit">
                            { (activeSubscription === undefined) &&
                                <div className="edit__error">

                                </div>
                            }
                            { (activeSubscription !== undefined) &&
                                <>
                                    <div className="edit__top-data">
                                        <div className="top-data__frequency-currency">
                                            <h4>{ 'Frequency' }</h4>
                                            <span>{ activeFrequency }</span>
                                            <h4>{ 'Currency' }</h4>
                                            <span className="caps">{ activeCurrency }</span>
                                        </div>
                                        <div className="edit__status">
                                            <h4>{ 'Status' }</h4>
                                            <span className={ activeSubscription?.status } >{ activeSubscription?.status }</span>
                                            <Button type="button"
                                                    color="error"
                                                    disabled={ processingSubscriptionUpdater || ( activeSubscription?.status === 'canceled' || processingCancel ) }
                                                    onClick={ toggleCancelConfirm }
                                                    variant="contained">
                                                { (!processingCancel) &&
                                                    <span>{ "Cancel" }</span>
                                                }
                                                { (processingCancel) &&
                                                    <CircularProgress sx={ {
                                                        width: '25px !important',
                                                        height: '25px !important',
                                                    } } />
                                                }

                                            </Button>
                                            <Confirm isOpen={ showCancelConfirmation }
                                                     onClose={ toggleCancelConfirm }
                                                     content="Are you sure you want to cancel this subscription?"
                                                     title="Cancel Subscription Confirmation!"
                                                     onConfirm={ cancelActiveSubscription }
                                            />
                                        </div>
                                    </div>
                                    <div className="edit__tabs">
                                        <div className="tabs__headers">
                                            <button type="button"
                                                    className={ (editTab === 1 ? 'active' : '') }
                                                    onClick={ (): void => handleEditTabChange(1) }>
                                                <span>{ "Update" }</span>
                                            </button>
                                            <button type="button"
                                                    className={ (editTab === 2 ? 'active' : '') }
                                                    onClick={ (): void => handleEditTabChange(2) }>
                                                <span>{ "Data" }</span>
                                            </button>
                                        </div>
                                    </div>
                                    { (editTab === 1) &&
                                        <>
                                            <div className="edit__discount">
                                                <span className="items__title">{ "Discounts" }</span>
                                                <SubscriptionManagerDiscountPanel
                                                    handleActiveDiscountChange={ handleActiveDiscountChange }
                                                    activeDiscounts={ activeDiscounts }
                                                />
                                            </div>
                                            <div className="edit__items">
                                                <span className="items__title">{ "Active Minis" }</span>
                                                { /* Four values are rendered per row (name, price,
                                                     discount, seats) but only three headers were
                                                     declared, so the rows wrapped and the discount
                                                     column sat under a "Used Seats" heading. */ }
                                                <div className="items__header">
                                                    <span>{ "Name" }</span>
                                                    <span>{ "Price" }</span>
                                                    <span>{ "Discount" }</span>
                                                    <span>{ "Seats" }</span>
                                                </div>
                                                <div className="items__items">
                                                    { activeSubscriptionProductData.map( (datum: any, index: number): JSX.Element => {
                                                        return (
                                                            <div className="items__item" key={ "new_subscription_items" + index }>
                                                                { determineMiniNameFromId(datum) }
                                                            </div>
                                                        );
                                                    } ) }
                                                </div>
                                                <div className="items__allPrice">
                                                    <div className="allPrice__price">
                                                        <span>{ `Subtotal` }</span>
                                                        <span>{ convertCurrencyToSymbol(activeCurrency) + activePrice.toFixed(2) }</span>
                                                    </div>
                                                    <div className="allPrice__price center">
                                                        <span>{ `Discount` }</span>
                                                        <span>{ getDiscountAmount() }</span>
                                                    </div>
                                                    <div className="allPrice__price right">
                                                        <span>{ `Total` }</span>
                                                        <span>{ convertCurrencyToSymbol(activeCurrency) + calculateTotalAmount() }</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="edit__actions">
                                                <div className="actions__extra">
                                                    <div className="extra__extra">
                                                        <div className="extra___top">
                                                            <span>{ "Disable Upgrade Proration" }</span>
                                                            <Switch disabled={ !subscriptionChanged || processingSubscriptionUpdater || ( activeSubscription?.status === 'canceled' ) || processingCancel }
                                                                    onChange={ handleUpgradeProrationToggle }
                                                                    value={ disableUpgradeProration } />
                                                        </div>
                                                        <div className="extra__bottom">
                                                            <div className="bottom__q">
                                                                <span className="q"  onClick={ toggleChargerHelperText } >{ "?" }</span>
                                                                { (showChargeHelperText) &&
                                                                    <span className="q__answer">
                                                                { "Disable Upgrade Proration - disables proration on subscription upgrade (price increase) and not charge the customer immediately. Next payment is at new price. Click ? to close" }
                                                            </span>
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="extra__extra">
                                                        <div className="extra___top">
                                                            <span>{ "Enable Downgrade Proration" }</span>
                                                            <Switch disabled={ !subscriptionChanged || processingSubscriptionUpdater || ( activeSubscription?.status === 'canceled' ) || processingCancel }
                                                                    onChange={ handleDowngradeRefundToggle }
                                                                    value={ enableDowngradeRefund } />
                                                        </div>
                                                        <div className="extra__bottom">
                                                            <div className="bottom__q">
                                                                <span className="q" onClick={ toggleRefundChargeHelperText } >{ "?" }</span>
                                                                { (showRefundChargeHelperText === true) &&
                                                                    <span className="q__answer">{ "Enable Downgrade Proration - enables proration on subscription downgrade (price decrease) and credit the customer. Next payment is at new price minus credit. Click ? to close" }</span>
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="actions__buttons">
                                                    <Button variant="contained"
                                                            className="buttons__update"
                                                            title={ "Subscription Update" }
                                                            disabled={ !subscriptionChanged || processingSubscriptionUpdater || ( activeSubscription?.status === 'canceled' ) || processingCancel }
                                                            onClick={ toggleUpdateConfirm }>
                                                        <span>{ "Update Subscription" }</span>
                                                        { (processingSubscriptionUpdater) &&
                                                            <CircularProgress sx={ {
                                                                width: '25px !important',
                                                                height: '25px !important',
                                                                margin: '0 0 0 10px'
                                                            } } />
                                                        }
                                                    </Button>
                                                    <Button variant="contained"
                                                            className="buttons__cancel"
                                                            title={ "Cancel Subscription Update" }
                                                            disabled={ !subscriptionChanged || processingSubscriptionUpdater || ( activeSubscription?.status === 'canceled' ) || processingCancel }
                                                            onClick={ cancelUpdate }>
                                                        <CancelPresentation />
                                                    </Button>
                                                    <Confirm isOpen={ showUpdateConfirm }
                                                             onClose={ toggleUpdateConfirm }
                                                             content="Are you sure you want to update this subscription?"
                                                             title="Update Subscription Confirmation!"
                                                             onConfirm={ updateActiveSubscription }
                                                    />
                                                    { (updateSubscriptionError === undefined) &&
                                                        <div className="actions__error">
                                                            <p>{ updateSubscriptionError }</p>
                                                        </div>
                                                    }
                                                </div>

                                            </div>
                                        </>
                                    }
                                    { (editTab === 2) &&
                                        <SubscriptionManagerActiveSubscriptionDataPanel
                                            activeCurrency={ activeCurrency }
                                            activeSubscription={ activeSubscription } />
                                    }
                                </>
                            }
                        </div>
                    }
                </div>
            </div>
        </div>
    );
}